import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import images from "../../utilities/images/images";
import { fetchMistakeTopicQuestions } from "./mistakeBookApi";
import chevronIcon from "./assets/mb-q-chevron.svg";
import resolvedCheckIcon from "./assets/mb-resolved-check.svg";
import "./MistakeBookTopicQuestionsPage.css";

const MISTAKE_TAG_STYLES = {
  "silly mistake": { bg: "rgba(255, 188, 76, 0.12)", color: "#9d6b15" },
  silly: { bg: "rgba(255, 188, 76, 0.12)", color: "#9d6b15" },
  conceptual: { bg: "#e8f5fe", color: "#257abb" },
  "conceptual gap": { bg: "#e8f5fe", color: "#257abb" },
  "calculation error": { bg: "#e7faec", color: "#168a34" },
  calculation: { bg: "#e7faec", color: "#168a34" },
  time: { bg: "#ffeeec", color: "#cb3824" },
  "time pressure": { bg: "#ffeeec", color: "#cb3824" },
  guessed: { bg: "rgba(194, 71, 251, 0.12)", color: "#9810fa" },
  "formula error": { bg: "rgba(255, 188, 76, 0.12)", color: "#9d6b15" },
  untagged: { bg: "rgba(201, 201, 201, 0.24)", color: "#667085" },
};

const DIFFICULTY_STYLES = {
  easy: { bg: "rgba(23, 178, 106, 0.12)", color: "#17b26a" },
  medium: { bg: "#fdf3e1", color: "#e17100" },
  hard: { bg: "#fff8f8", color: "#f04438" },
  tough: { bg: "#fff8f8", color: "#f04438" },
};

const getTagStyle = (label, map) => {
  const key = String(label || "").trim().toLowerCase();
  return map[key] || { bg: "rgba(152, 162, 179, 0.12)", color: "#475569" };
};

const toPlainText = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";

  if (typeof document !== "undefined") {
    const el = document.createElement("div");
    el.innerHTML = raw;
    return (el.textContent || el.innerText || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  return raw
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&plusmn;/g, "±")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/\s+/g, " ")
    .trim();
};

const MistakeBookTopicQuestionsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const state = location.state || {};

  const topic = state.topic || {};
  const subject = state.subject || {};
  const subjectName =
    state.subjectName || subject.name || topic.subjectName || "Physics";
  const topicName = state.topicName || topic.name || "";
  const year = state.year;
  const subjectId = state.subjectId || subject.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(
    state.activeTab === "resolved" ? "resolved" : "mistakes"
  );
  const [mistakes, setMistakes] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [mistakeCount, setMistakeCount] = useState(
    Number(topic.mistakes) || 0
  );
  const [resolvedCount, setResolvedCount] = useState(
    Number(topic.resolved) || 0
  );

  const loadQuestions = useCallback(async () => {
    if (!topicName) {
      setLoading(false);
      setError("Topic name is missing.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const payload = await fetchMistakeTopicQuestions({
        paper_type: 22,
        subject: subjectName,
        topic_name: topicName,
      });
      setMistakes(payload.mistakes || []);
      setResolved(payload.resolved || []);
      setMistakeCount(payload.mistakeCount);
      setResolvedCount(payload.resolvedCount);
    } catch (err) {
      setMistakes([]);
      setResolved([]);
      setError(
        err?.response?.data?.message ||
          "Unable to load questions for this topic. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [subjectName, topicName]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const visibleQuestions = useMemo(
    () => (activeTab === "resolved" ? resolved : mistakes),
    [activeTab, mistakes, resolved]
  );

  const handleBack = () => {
    history.push("/practice/mistake-book", {
      year,
      subjectId,
    });
  };

  const handleOpenQuestion = (question, index) => {
    history.push("/practice/mistake-book/question-detail", {
      fromMistakeBook: true,
      paper_type: 22,
      paperType: 22,
      subject,
      subjectName,
      topic,
      topicName,
      year,
      subjectId,
      activeTab,
      qid: question.qid,
      question,
      questionIndex: index,
      questions: visibleQuestions,
      listPath: "/practice/mistake-book/topic-questions",
    });
  };

  return (
    <div className="mb-topic-questions-page">
      <div className="mb-topic-questions-sticky">
        <header className="mb-topic-questions-header">
          <button
            type="button"
            className="mb-topic-questions-header__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <img src={images.new_backarrow} alt="" />
          </button>
          <h1 className="mb-topic-questions-header__title">Mistake Book</h1>
          <div className="mb-topic-questions-header__spacer" aria-hidden="true" />
        </header>

        <nav className="mb-topic-questions-tabs" aria-label="Question filters">
          <button
            type="button"
            className={`mb-topic-questions-tab${
              activeTab === "mistakes" ? " is-active" : ""
            }`}
            onClick={() => setActiveTab("mistakes")}
          >
            Mistakes ({mistakeCount})
          </button>
          <button
            type="button"
            className={`mb-topic-questions-tab${
              activeTab === "resolved" ? " is-active" : ""
            }`}
            onClick={() => setActiveTab("resolved")}
          >
            Resolved ({resolvedCount})
          </button>
        </nav>
      </div>

      <div className="mb-topic-questions-body">
        {loading ? (
          <div className="mb-topic-questions-loader">
            <ClipLoader size={40} color="#C61C26" />
          </div>
        ) : (
          <>
            {error ? <p className="mb-topic-questions-error">{error}</p> : null}

            {visibleQuestions.length ? (
              <div className="mb-topic-questions-list">
                {visibleQuestions.map((question, index) => {
                  const mistakeStyle = getTagStyle(
                    question.mistakeTag,
                    MISTAKE_TAG_STYLES
                  );
                  const difficultyStyle = getTagStyle(
                    question.difficulty,
                    DIFFICULTY_STYLES
                  );
                  const displayText = toPlainText(question.text);

                  return (
                    <button
                      type="button"
                      key={question.id}
                      className="mb-topic-question-card"
                      onClick={() => handleOpenQuestion(question, index)}
                    >
                      <div className="mb-topic-question-card__main">
                        <div className="mb-topic-question-card__index">
                          <span>{question.number || index + 1}</span>
                        </div>
                        <div className="mb-topic-question-card__content">
                          <p className="mb-topic-question-card__text">
                            {displayText}
                          </p>
                          {(question.mistakeTag ||
                            question.difficulty ||
                            activeTab === "resolved") && (
                            <div className="mb-topic-question-card__tags">
                              {question.mistakeTag ? (
                                <span
                                  className="mb-topic-question-card__tag"
                                  style={{
                                    background: mistakeStyle.bg,
                                    color: mistakeStyle.color,
                                  }}
                                >
                                  {question.mistakeTag}
                                </span>
                              ) : null}
                              {question.difficulty ? (
                                <span
                                  className="mb-topic-question-card__tag"
                                  style={{
                                    background: difficultyStyle.bg,
                                    color: difficultyStyle.color,
                                  }}
                                >
                                  {question.difficulty}
                                </span>
                              ) : null}
                              {activeTab === "resolved" ? (
                                <span className="mb-topic-question-card__tag mb-topic-question-card__tag--resolved">
                                  <img src={resolvedCheckIcon} alt="" />
                                  Resolved
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      <span
                        className="mb-topic-question-card__action"
                        aria-hidden="true"
                      >
                        <img src={chevronIcon} alt="" />
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="mb-topic-questions-empty">
                {activeTab === "resolved"
                  ? "No resolved questions yet for this topic."
                  : "No mistake questions found for this topic."}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MistakeBookTopicQuestionsPage;
